import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/apiClient';

// Generate unique visitor ID
const generateVisitorId = () => {
  return 'v_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

// Generate session ID
const generateSessionId = () => {
  return 's_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

// Cookie helpers
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
  return null;
};

// Get or create visitor ID
const getVisitorId = () => {
  let visitorId = getCookie('bc_visitor_id');
  if (!visitorId) {
    visitorId = generateVisitorId();
    setCookie('bc_visitor_id', visitorId, 365 * 2); // 2 years
  }
  return visitorId;
};

// Get or create session ID (expires after 30 min of inactivity)
const getSessionId = () => {
  const sessionKey = 'bc_session_id';
  const sessionTimeKey = 'bc_session_time';
  
  let sessionId = sessionStorage.getItem(sessionKey);
  const lastActivity = sessionStorage.getItem(sessionTimeKey);
  const now = Date.now();
  
  // Session expires after 30 minutes of inactivity
  if (!sessionId || !lastActivity || (now - parseInt(lastActivity)) > 30 * 60 * 1000) {
    sessionId = generateSessionId();
    sessionStorage.setItem(sessionKey, sessionId);
  }
  
  sessionStorage.setItem(sessionTimeKey, now.toString());
  return sessionId;
};

// Check if user has given consent
export const hasAnalyticsConsent = () => {
  return getCookie('bc_analytics_consent') === 'true';
};

// Set analytics consent
export const setAnalyticsConsent = (consent) => {
  setCookie('bc_analytics_consent', consent ? 'true' : 'false', 365);
  if (consent) {
    // Trigger initial tracking when consent is given
    window.dispatchEvent(new CustomEvent('analyticsConsentGiven'));
  }
};

// Get URL parameters
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    qr_code_id: params.get('qr') || params.get('qr_code_id'),
  };
};

// Event queue for batch processing
let eventQueue = {
  pageViews: [],
  events: [],
  cartEvents: [],
  sessionUpdates: []
};

// Flush event queue to server
const flushEventQueue = async () => {
  if (!hasAnalyticsConsent()) return;
  
  const batch = { ...eventQueue };
  eventQueue = { pageViews: [], events: [], cartEvents: [], sessionUpdates: [] };
  
  const hasData = batch.pageViews.length > 0 || batch.events.length > 0 || 
                  batch.cartEvents.length > 0 || batch.sessionUpdates.length > 0;
  
  if (!hasData) return;
  
  try {
    await api.post('/analytics/track/batch', {
      page_views: batch.pageViews,
      events: batch.events,
      cart_events: batch.cartEvents,
      session_updates: batch.sessionUpdates
    });
  } catch (error) {
    console.error('Analytics batch send failed:', error);
    // Re-queue events on failure (limited to prevent memory issues)
    if (eventQueue.pageViews.length < 100) {
      eventQueue.pageViews.push(...batch.pageViews.slice(0, 50));
    }
  }
};

// Set up batch processing interval
let flushInterval = null;
const startBatchProcessing = () => {
  if (flushInterval) return;
  flushInterval = setInterval(flushEventQueue, 5000); // Flush every 5 seconds
  
  // Also flush on page unload
  window.addEventListener('beforeunload', flushEventQueue);
  window.addEventListener('pagehide', flushEventQueue);
};

// Analytics hook for React components
export const useAnalytics = () => {
  const location = useLocation();
  const visitorId = useRef(getVisitorId());
  const sessionId = useRef(getSessionId());
  const pageStartTime = useRef(Date.now());
  const scrollDepth = useRef(0);
  const interactionCount = useRef(0);
  const [consentGiven, setConsentGiven] = useState(hasAnalyticsConsent());

  // Track page view
  const trackPageView = useCallback((customPath) => {
    if (!hasAnalyticsConsent()) return;
    
    const params = getUrlParams();
    const pageView = {
      visitor_id: visitorId.current,
      session_id: sessionId.current,
      page_path: customPath || location.pathname,
      page_title: document.title,
      referrer: document.referrer,
      utm_source: params.utm_source,
      utm_medium: params.utm_medium,
      utm_campaign: params.utm_campaign,
      utm_content: params.utm_content,
      qr_code_id: params.qr_code_id,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      consent_given: true
    };
    
    eventQueue.pageViews.push(pageView);
    pageStartTime.current = Date.now();
    scrollDepth.current = 0;
    interactionCount.current = 0;
  }, [location.pathname]);

  // Track custom event
  const trackEvent = useCallback((eventType, eventCategory, eventLabel, eventValue, metadata) => {
    if (!hasAnalyticsConsent()) return;
    
    const event = {
      visitor_id: visitorId.current,
      session_id: sessionId.current,
      page_path: location.pathname,
      event_type: eventType,
      event_category: eventCategory,
      event_label: eventLabel,
      event_value: eventValue,
      metadata: metadata
    };
    
    eventQueue.events.push(event);
    interactionCount.current++;
  }, [location.pathname]);

  // Track button/link click
  const trackClick = useCallback((element, customLabel) => {
    if (!hasAnalyticsConsent()) return;
    
    const label = customLabel || element?.innerText?.slice(0, 100) || element?.getAttribute('aria-label');
    const event = {
      visitor_id: visitorId.current,
      session_id: sessionId.current,
      page_path: location.pathname,
      event_type: 'click',
      event_category: element?.tagName?.toLowerCase() || 'unknown',
      event_label: label,
      element_id: element?.id,
      element_class: element?.className?.slice?.(0, 200),
      element_text: element?.innerText?.slice(0, 100)
    };
    
    eventQueue.events.push(event);
    interactionCount.current++;
  }, [location.pathname]);

  // Track form submission
  const trackSubmit = useCallback((formName, success, metadata) => {
    if (!hasAnalyticsConsent()) return;
    
    const event = {
      visitor_id: visitorId.current,
      session_id: sessionId.current,
      page_path: location.pathname,
      event_type: 'submit',
      event_category: 'form',
      event_label: formName,
      event_value: success ? 1 : 0,
      metadata: metadata
    };
    
    eventQueue.events.push(event);
    interactionCount.current++;
  }, [location.pathname]);

  // Track cart event
  const trackCartEvent = useCallback((eventType, item, cartTotal, cartItemsCount, orderId) => {
    if (!hasAnalyticsConsent()) return;
    
    const cartEvent = {
      visitor_id: visitorId.current,
      session_id: sessionId.current,
      event_type: eventType,
      item_id: item?.id,
      item_name: item?.name,
      item_category: item?.category,
      item_price: item?.price,
      quantity: item?.quantity,
      cart_total: cartTotal,
      cart_items_count: cartItemsCount,
      order_id: orderId,
      metadata: item?.modifiers ? { modifiers: item.modifiers } : null
    };
    
    eventQueue.cartEvents.push(cartEvent);
  }, []);

  // Update session with time on page
  const updateSession = useCallback(() => {
    if (!hasAnalyticsConsent()) return;
    
    const timeOnPage = Math.floor((Date.now() - pageStartTime.current) / 1000);
    
    const update = {
      visitor_id: visitorId.current,
      session_id: sessionId.current,
      page_path: location.pathname,
      time_on_page: timeOnPage,
      scroll_depth: scrollDepth.current,
      interactions_count: interactionCount.current
    };
    
    // Remove old session updates for same page
    eventQueue.sessionUpdates = eventQueue.sessionUpdates.filter(
      s => s.page_path !== location.pathname || s.session_id !== sessionId.current
    );
    eventQueue.sessionUpdates.push(update);
  }, [location.pathname]);

  // Track scroll depth
  useEffect(() => {
    if (!consentGiven) return;
    
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight > 0) {
        const currentDepth = Math.round((window.scrollY / scrollHeight) * 100);
        if (currentDepth > scrollDepth.current) {
          scrollDepth.current = currentDepth;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [consentGiven]);

  // Track page view on route change
  useEffect(() => {
    if (!consentGiven) return;
    
    // Update session for previous page before tracking new one
    if (pageStartTime.current) {
      updateSession();
    }
    
    trackPageView();
    startBatchProcessing();
  }, [location.pathname, trackPageView, updateSession, consentGiven]);

  // Listen for consent changes
  useEffect(() => {
    const handleConsentGiven = () => {
      setConsentGiven(true);
      trackPageView();
      startBatchProcessing();
    };
    
    window.addEventListener('analyticsConsentGiven', handleConsentGiven);
    return () => window.removeEventListener('analyticsConsentGiven', handleConsentGiven);
  }, [trackPageView]);

  // Update session periodically
  useEffect(() => {
    if (!consentGiven) return;
    
    const interval = setInterval(updateSession, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [updateSession, consentGiven]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      updateSession();
      flushEventQueue();
    };
  }, [updateSession]);

  return {
    trackEvent,
    trackClick,
    trackSubmit,
    trackCartEvent,
    trackPageView,
    visitorId: visitorId.current,
    sessionId: sessionId.current,
    hasConsent: consentGiven
  };
};

// Global click tracker (attach to document)
export const initGlobalClickTracking = () => {
  if (!hasAnalyticsConsent()) return;
  
  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  
  document.addEventListener('click', (e) => {
    if (!hasAnalyticsConsent()) return;
    
    const target = e.target.closest('button, a, [data-track], [type="submit"]');
    if (!target) return;
    
    const event = {
      visitor_id: visitorId,
      session_id: sessionId,
      page_path: window.location.pathname,
      event_type: 'click',
      event_category: target.tagName.toLowerCase(),
      event_label: target.innerText?.slice(0, 100) || target.getAttribute('aria-label'),
      element_id: target.id || target.getAttribute('data-testid'),
      element_class: target.className?.slice?.(0, 200),
      element_text: target.innerText?.slice(0, 100)
    };
    
    eventQueue.events.push(event);
  }, { passive: true });
};

export default useAnalytics;
